import * as Sentry from '@sentry/browser';

const SENTRY_DSN =
  'https://af2038b0f437dfe3c75119fbed73d65e@o4506260410335232.ingest.us.sentry.io/4506260415578112';

const APP_VERSION = '0.0.1';

function isLocalHost() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function getStoredInstance() {
  try {
    return localStorage.getItem('instance_name');
  } catch (_) {
    return null;
  }
}

function resolveEnvironment() {
  if (isLocalHost()) return 'development';
  return getStoredInstance() || 'production';
}

const appEnv = resolveEnvironment();
const isDev = appEnv === 'development';

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/[^/]+\.assistgo\.de/,
  ],
  attachStacktrace: true,
  environment: appEnv,
  // Send events directly to Sentry ingest (no tunnel: the backend /tunnel/sentry
  // route returns 405 and is cross-origin for the Netlify-hosted add-in anyway).
  // Keep error capture at 100%; sample tracing to limit overhead in Outlook.
  tracesSampleRate: isDev ? 0 : 0.2,
  release: `outlook-addin@${APP_VERSION}`,
  enabled: !isDev,
  initialScope: {
    tags: { app: 'outlook-addin' },
  },
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
  ],
  beforeSend(event, hint) {
    const message = hint?.originalException?.message || event?.exception?.values?.[0]?.value || '';
    // Expected auth/session failures — not actionable production bugs.
    if (
      message.includes('Not authenticated') ||
      message.includes('Token refresh failed') ||
      message.includes('No refresh token available') ||
      message.includes('Invalid credentials') ||
      message.includes('Login was cancelled') ||
      message.includes('popup was blocked')
    ) {
      return null;
    }
    return event;
  },
});

/**
 * Attach the authenticated user to Sentry events.
 */
export function setSentryUser({ email, instance } = {}) {
  if (!email && !instance) {
    clearSentryUser();
    return;
  }

  if (email) {
    Sentry.setUser({
      email,
      username: email,
    });
  }

  if (instance) {
    Sentry.setTag('instance', instance);
    Sentry.setContext('tenant', { instance });
  }
}

/**
 * Clear user identity on logout.
 */
export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setTag('instance', '');
  Sentry.setContext('tenant', null);
}

/**
 * Capture an exception with optional extra context.
 * Prefer this for unexpected failures; handled UI/API errors should stay in the UI.
 */
export function captureException(error, context) {
  if (!(error instanceof Error) && typeof error !== 'string') {
    Sentry.captureException(new Error(String(error)), context);
    return;
  }
  Sentry.captureException(error, context);
}

export { Sentry };
